import React, { useMemo } from 'react';
import { Box, TypographyVariant } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AccessTime from '@mui/icons-material/AccessTime';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import PowerOffOutlined from '@mui/icons-material/PowerOffOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import classNames from 'classnames';
import { classifySignableSigners } from './helper';
import {
  getSignableStatus,
  SignableStatus as SignableStatusEnum,
  SignableSyncStatus,
} from '../../enums/Signables';
import { SignerSchema } from '../../definitions/schemas';
import SignableStatusTooltip from './SignableStatusTooltip';
import TextWithIcon from '../TextWithIcon';
import { useI18n } from '../../lib/hook/useI18n';

const useStyles = makeStyles(() => ({
  error: {
    color: 'var(--error-color-dark)',
  },
  success: {
    color: 'var(--success-color-dark)',
  },
}));

interface Props {
  disabled?: boolean;
  disablePortal?: boolean;
  inline?: boolean;
  isInhouseSignable?: boolean;
  shortLabel?: boolean;
  signers: SignerSchema[];
  status?: SignableStatusEnum;
  syncStatus?: SignableSyncStatus;
  variant?: TypographyVariant;
}

const SignableStatus: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  const {
    disabled = false,
    disablePortal,
    inline = false,
    isInhouseSignable = false,
    shortLabel = false,
    signers,
    status,
    syncStatus,
    variant = 'body2',
  } = props;

  const [signed] = useMemo(() => classifySignableSigners(signers), [signers]);

  const [hasStatusError, hasSyncStatusError, isCompleted] = useMemo(
    () => getSignableStatus(status, syncStatus),
    [status, syncStatus]
  );

  const hasError = hasStatusError || hasSyncStatusError;
  const iconFontSize = variant === 'body2' ? 'small' : undefined;

  const renderStatus = (): JSX.Element => {
    const totalSigners = signers.length;
    const totalCompletedSignatures = signed.length;

    if (isCompleted) {
      return (
        <Box
          className={classNames({ [classes.success]: !disabled })}
          display={inline ? 'inline-block' : undefined}
        >
          <TextWithIcon
            color="inherit"
            disableTextPadding
            icon={
              <CheckCircleOutline color="inherit" fontSize={iconFontSize} />
            }
            text={t('esignature.box.completed')}
            variant={variant}
          />
        </Box>
      );
    }

    return (
      <Box
        color={disabled ? 'text.disabled' : undefined}
        display={inline ? 'inline-block' : undefined}
      >
        <TextWithIcon
          color={disabled ? 'inherit' : 'textSecondary'}
          disableTextPadding
          icon={<AccessTime color="inherit" fontSize={iconFontSize} />}
          text={t('esignature.box.signed', {
            completed: totalCompletedSignatures,
            total: totalSigners,
          })}
          variant={variant}
        />
      </Box>
    );
  };

  const renderErrorStatus = (): JSX.Element => {
    let Icon = PowerOffOutlined;
    let label: React.ReactNode =
      shortLabel || isInhouseSignable
        ? 'Disconnected'
        : 'Disconnected from DocuSign';

    if (hasStatusError) {
      let signableName = t('esignature.box.signable_document');
      if (!isInhouseSignable) {
        signableName = t('esignature.box.envelope');
      }

      label =
        status === SignableStatusEnum.DECLINED
          ? 'Declined by signer'
          : t('esignature.box.voided', { name: signableName });

      Icon = ReportProblemOutlined;
    }

    return (
      <Box className={classNames({ [classes.error]: !disabled })}>
        <TextWithIcon
          bold
          color="inherit"
          disableTextPadding
          icon={<Icon color="inherit" fontSize={iconFontSize} />}
          text={label}
          variant={variant}
        />
      </Box>
    );
  };

  if (hasError) {
    return renderErrorStatus();
  }

  return (
    <SignableStatusTooltip disablePortal={disablePortal} signers={signers}>
      {renderStatus()}
    </SignableStatusTooltip>
  );
};

export default SignableStatus;
